import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { F0Box, F0Dialog, F0Text } from '@factorialco/f0-react'
import { File, FileFilled } from '@factorialco/f0-react/icons/app'
import {
  DetailsItemsList,
  DetailsItemType,
  OneEmptyState,
  ResourceHeader,
  Spinner,
  Tabs,
} from '@factorialco/f0-react/dist/experimental'
import {
  AtsVacancyDetailDocument,
  AtsVacancyDetailPersonFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  getDeadlineIcon,
  getDaysOpen,
  getStatusVariant,
  buildDetailAvatarListItem,
} from 'modules/ats/lib/utils/vacancies'

import { useAtsContext } from 'modules/ats/lib/atsContext'
import { RequisitionNote } from './RequisitionNote'

const formatSalaryAmount = (
  amountInCents: number | null | undefined,
  currency: string | null | undefined
): string => {
  if (amountInCents == null) return '-'
  const amount = amountInCents / 100
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
    } catch {
      return `${amount} ${currency}`
    }
  }
  return String(amount)
}

type MetadataItem =
  | { label: string; value: { type: 'text'; content: string } }
  | { label: string; value: { type: 'date'; formattedDate: string; icon?: 'warning' | 'critical' } }

const buildPersonItem = (
  person: AtsVacancyDetailPersonFragment | null | undefined,
  title: string,
  href?: string
): DetailsItemType => {
  if (!person)
    return {
      title,
      content: { type: 'item', text: '-' },
    }

  return {
    title,
    content: {
      ...(href && { action: { type: 'navigate' as const, href } }),
      type: 'person',
      firstName: person.firstName ?? '',
      lastName: person.lastName ?? '',
      avatarUrl: buildUrl(person.avatar?.url ?? ''),
    },
  }
}

const VacancyDetailPage = () => {
  const { vacancy_id } = useParams()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { requisitionsEnabled } = useAtsContext()
  const [activeTabId, setActiveTabId] = useState('details')

  const { data, loading } = useQuery(AtsVacancyDetailDocument, {
    variables: { id: vacancy_id! },
  })

  const vacancy = data?.ats.vacancy
  const statusVariant = getStatusVariant(vacancy?.status ?? '')
  const isRequisition = vacancy?.originType === 'requisition'

  const metadata = useMemo((): MetadataItem[] => {
    if (!vacancy) return []

    const items: MetadataItem[] = []

    items.push({
      label: i18n.t('ats.vacancies.table.columns.deadline'),
      value: {
        type: 'date',
        formattedDate: convertDate(vacancy.deadline ?? '', 'backend', 'ui_abbreviated_month_year'),
        icon: getDeadlineIcon(vacancy.deadline ?? '', vacancy.status),
      },
    })

    if (vacancy.jobPosting) {
      items.push({
        label: i18n.t('ats.vacancies.table.columns.job_opening'),
        value: { type: 'text', content: vacancy.jobPosting.title },
      })
    }

    return items
  }, [vacancy])

  const vacancyDetails = useMemo((): DetailsItemType[] => {
    if (!vacancy) return []

    const items: DetailsItemType[] = []

    if (vacancy.recruiters.length === 1) {
      items.push(
        buildPersonItem(vacancy.recruiters[0], i18n.t('ats.vacancies.table.columns.recruiters'))
      )
    } else {
      items.push(
        buildDetailAvatarListItem(
          vacancy.recruiters,
          i18n.t('ats.vacancies.table.columns.recruiters')
        )
      )
    }

    if (vacancy.atsVacancyHiringManagers.length === 1) {
      items.push(
        buildPersonItem(
          vacancy.atsVacancyHiringManagers[0],
          i18n.t('ats.vacancies.table.columns.hiring_managers')
        )
      )
    } else {
      items.push(
        buildDetailAvatarListItem(
          vacancy.atsVacancyHiringManagers,
          i18n.t('ats.vacancies.table.columns.hiring_managers')
        )
      )
    }

    const lineManagerHref = vacancy.lineManager
      ? navigation.employees.id.fullPath({ id: vacancy.lineManager.id })
      : undefined
    items.push(
      buildPersonItem(
        vacancy.lineManager,
        i18n.t('ats.vacancies.table.columns.line_manager'),
        lineManagerHref
      )
    )

    const jobOpeningContent = vacancy.jobPosting
      ? {
          type: 'item' as const,
          text: vacancy.jobPosting.title,
          action: {
            type: 'navigate' as const,
            href: navigation.ats.jobs.jobsId.applications.fullPath({
              jobId: vacancy.jobPosting.id,
            }),
          },
        }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.job_opening'),
      content: jobOpeningContent,
    })

    const originContent =
      vacancy.originType === 'requisition' && requisitionsEnabled && vacancy.requisition
        ? {
            type: 'item' as const,
            text: vacancy.originTypeLabel,
            icon: FileFilled,
            action: {
              type: 'navigate' as const,
              href: navigation.ats.hiringPlan.requisitions.requisitionDetail.fullPath({
                requisition_id: vacancy.requisition.id,
              }),
            },
          }
        : {
            type: 'item' as const,
            text: vacancy.originTypeLabel,
            icon: vacancy.originType === 'requisition' ? FileFilled : File,
          }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.origin'),
      content: originContent,
    })

    const daysOpen = getDaysOpen(vacancy.processStartedAt)
    items.push({
      title: i18n.t('ats.vacancies.table.columns.days_open'),
      content: { type: 'item', text: daysOpen !== null ? `${daysOpen}` : '-' },
    })

    const candidateEmployeeItem = vacancy.employee
      ? buildPersonItem(vacancy.employee, i18n.t('ats.vacancies.table.columns.candidate_employee'))
      : vacancy.application
        ? {
            title: i18n.t('ats.vacancies.table.columns.candidate_employee'),
            content: {
              type: 'item' as const,
              text: vacancy.application.fullName,
              action: {
                type: 'navigate' as const,
                href: navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
                  jobId: vacancy.application.jobPosting.id,
                  application_id: vacancy.application.id,
                }),
              },
            },
          }
        : {
            title: i18n.t('ats.vacancies.table.columns.candidate_employee'),
            content: { type: 'item' as const, text: '-' },
          }
    items.push(candidateEmployeeItem)

    const vacancyTypeContent = vacancy.vacancyType
      ? {
          type: 'dot-tag' as const,
          text: vacancy.vacancyTypeLabel,
          color:
            vacancy.vacancyType === 'new_position' ? ('flubber' as const) : ('purple' as const),
        }
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.vacancy_type'),
      content: vacancyTypeContent,
    })

    if (vacancy.vacancyType === 'backfill' && vacancy.employeeReplaced) {
      const employeeReplacedHref = navigation.employees.id.fullPath({
        id: vacancy.employeeReplaced.id,
      })
      items.push(
        buildPersonItem(
          vacancy.employeeReplaced,
          i18n.t('ats.vacancies.detail.employee_to_backfill'),
          employeeReplacedHref
        )
      )
    }

    return items
  }, [vacancy, navigation, requisitionsEnabled])

  const roleDetails = useMemo((): DetailsItemType[] => {
    if (!vacancy) return []

    const items: DetailsItemType[] = []

    items.push({
      title: i18n.t('ats.vacancies.detail.role_label'),
      content: {
        type: 'item',
        text: vacancy.jobCatalogTreeRole?.name ?? '-',
      },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.level_label'),
      content: {
        type: 'item',
        text: vacancy.jobCatalogTreeLevel?.name ?? '-',
      },
    })

    const teamContent = vacancy.team?.name
      ? [{ type: 'team' as const, name: vacancy.team.name }]
      : { type: 'item' as const, text: '-' }
    items.push({
      title: i18n.t('ats.vacancies.table.columns.team'),
      content: teamContent,
    })

    items.push({
      title: i18n.t('ats.vacancies.table.columns.market'),
      content: {
        type: 'item',
        text: vacancy.location?.name ?? '-',
      },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.workplace_type'),
      content: { type: 'item', text: vacancy.workplaceTypeLabel ?? '-' },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.agreement_type'),
      content: { type: 'item', text: vacancy.contractTypeLabel ?? '-' },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.schedule_type'),
      content: { type: 'item', text: vacancy.scheduleTypeLabel ?? '-' },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.salary_period'),
      content: { type: 'item', text: vacancy.salaryPeriodLabel ?? '-' },
    })

    items.push({
      title: i18n.t('ats.vacancies.detail.salary_type'),
      content: { type: 'item', text: vacancy.salaryTypeLabel ?? '-' },
    })

    const salaryFrom = formatSalaryAmount(vacancy.salaryFromAmountInCents, vacancy.salaryCurrency)
    const salaryTo = formatSalaryAmount(vacancy.salaryToAmountInCents, vacancy.salaryCurrency)
    const amountText =
      vacancy.salaryFromAmountInCents != null && vacancy.salaryToAmountInCents != null
        ? `${salaryFrom} - ${salaryTo}`
        : vacancy.salaryFromAmountInCents != null
          ? salaryFrom
          : vacancy.salaryToAmountInCents != null
            ? salaryTo
            : '-'
    items.push({
      title: i18n.t('ats.vacancies.detail.amount'),
      content: { type: 'item', text: amountText },
    })

    return items
  }, [vacancy])

  const timelineDetails = useMemo((): DetailsItemType[] => {
    if (!vacancy) return []

    const items: DetailsItemType[] = []

    if (isRequisition && vacancy.requisition?.createdAt) {
      items.push({
        title: i18n.t('ats.vacancies.detail.requisition_date'),
        content: {
          type: 'item',
          text: convertDate(
            vacancy.requisition.createdAt,
            'backend_datetime',
            'ui_abbreviated_month_year'
          ),
        },
      })
    }

    if (isRequisition && vacancy.requisition?.materializedApprovalFlow) {
      const approvalDate = vacancy.requisition.materializedApprovalFlow.finalDecisionAt
      items.push({
        title: i18n.t('ats.vacancies.detail.approval_date'),
        content: {
          type: 'item',
          text: approvalDate
            ? convertDate(approvalDate, 'backend_datetime', 'ui_abbreviated_month_year')
            : '-',
        },
      })
    }

    items.push({
      title: i18n.t('ats.vacancies.detail.start_date'),
      content: {
        type: 'item',
        text: vacancy.processStartedAt
          ? convertDate(vacancy.processStartedAt, 'backend_datetime', 'ui_abbreviated_month_year')
          : '-',
      },
    })

    const closeDate = vacancy.status === 'cancelled' ? vacancy.cancelledAt : vacancy.hiredAt
    items.push({
      title: i18n.t('ats.vacancies.detail.close_date'),
      content: {
        type: 'item',
        text: closeDate
          ? convertDate(closeDate, 'backend_datetime', 'ui_abbreviated_month_year')
          : '-',
      },
    })

    return items
  }, [vacancy, isRequisition])

  const handleClose = () => navigate(navigation.ats.hiringPlan.vacancies.fullPath())

  const tabs = [
    { id: 'details', label: i18n.t('ats.vacancies.detail.tabs.details') },
    { id: 'notes', label: i18n.t('ats.vacancies.detail.tabs.notes') },
  ]

  const detailsContent = (
    <F0Box display="flex" flexDirection="column" gap="2xl">
      <DetailsItemsList
        title={i18n.t('ats.vacancies.detail.sections.vacancy_details')}
        details={vacancyDetails}
        tableView
      />
      <DetailsItemsList
        title={i18n.t('ats.vacancies.detail.sections.role_details')}
        details={roleDetails}
        tableView
      />
      <DetailsItemsList
        title={i18n.t('ats.vacancies.detail.sections.timeline')}
        details={timelineDetails}
        tableView
      />
    </F0Box>
  )

  return (
    <F0Dialog position="right" isOpen onClose={handleClose}>
      {loading && (
        <F0Box padding="xl" display="flex" justifyContent="center" alignItems="center" grow>
          <Spinner size="large" />
        </F0Box>
      )}
      {!loading && !vacancy && (
        <OneEmptyState
          variant="warning"
          title={i18n.t('ats.vacancies.detail.could_not_load_data')}
        />
      )}
      {!loading && vacancy && (
        <F0Box display="flex" flexDirection="column" gap="lg">
          <ResourceHeader
            title={vacancy?.name ?? ''}
            status={
              statusVariant && vacancy
                ? {
                    label: vacancy.statusLabel,
                    text: vacancy.statusLabel,
                    variant: statusVariant,
                  }
                : undefined
            }
            metadata={metadata}
          />
          {isRequisition ? (
            <>
              <Tabs tabs={tabs} activeTabId={activeTabId} setActiveTabId={setActiveTabId} />
              {activeTabId === 'details' && detailsContent}
              {activeTabId === 'notes' && (
                <F0Box padding="md">
                  {vacancy.requisition?.note ? (
                    <RequisitionNote requisition={vacancy.requisition} />
                  ) : (
                    <F0Text
                      variant="description"
                      content={i18n.t('ats.vacancies.detail.no_notes')}
                    />
                  )}
                </F0Box>
              )}
            </>
          ) : (
            detailsContent
          )}
        </F0Box>
      )}
    </F0Dialog>
  )
}

export default VacancyDetailPage
